// Shared helpers for matching recipe ingredients to grocery products and
// rendering localized names/images. Used by RecipeDrawer (real grocery list)
// and RecipeContent (promo landing — uses a saved product subset).

import { PRODUCT_METADATA } from './groceryConstants';
import { getCurrentLanguage } from '../services/i18n';

// Resolve a product image URL from a grocery item. Source of truth is the
// Typesense product doc; we try every shape the doc may take across the
// pipeline:
//   - `selectedSize.image` (Phase 5 — full grocery flow, used by RecipeDrawer)
//   - `sizes[].image`      (post-Phase-5 size array)
//   - `product.sizes[].image` / `product.thumbnail` / `product.image`
//     (Phases 1-3 — `matchIngredientsForBatch`, used by PromoLandingPage —
//     keeps the raw Typesense doc nested under `item.product`)
//   - `thumbnail` / `image` at the top level (defensive)
// PRODUCT_METADATA is intentionally NOT used: it returns local emoji/icon
// images that don't match the Typesense product photos shown in RecipeDrawer.
export const getProductImage = (item) => {
  if (!item || item.unmatched === true || item.error) {
    return '/assets/products/no-image.jpg';
  }
  if (item.selectedSize?.image) return item.selectedSize.image;

  if (Array.isArray(item.sizes)) {
    const firstWithImage = item.sizes.find((s) => s?.image);
    if (firstWithImage) return firstWithImage.image;
  }

  const product = item.product;
  if (product) {
    if (Array.isArray(product.sizes)) {
      const firstWithImage = product.sizes.find((s) => s?.image);
      if (firstWithImage) return firstWithImage.image;
    }
    if (product.thumbnail) return product.thumbnail;
    if (product.image) return product.image;
  }

  if (item.thumbnail) return item.thumbnail;
  if (item.image) return item.image;

  return '/assets/products/no-image.jpg';
};

// Localized product name from item.lang map; falls back to displayName / productName.
export const getLocalizedProductName = (item) => {
  const currentLang = getCurrentLanguage();
  let langMap = item?.lang;
  if (typeof langMap === 'string') {
    try { langMap = JSON.parse(langMap); } catch { langMap = null; }
  }
  if (langMap && typeof langMap === 'object') {
    const exact = langMap[currentLang];
    if (exact) return exact;
    const base = currentLang?.split('-')[0];
    if (base && langMap[base]) return langMap[base];
  }
  return item?.displayName || item?.productName || null;
};

// Match a recipe ingredient to a grocery product by originalName / name / allOriginals.
// productList is the same structure used by MealPlanContext: [{ items: [...] }, ...].
export const findMatchedProduct = (ingredient, productList) => {
  if (!ingredient || !productList?.length) return null;

  const ingName = (ingredient.product || ingredient.name || '').toLowerCase().trim();
  if (!ingName) return null;

  for (const category of productList) {
    if (!category?.items) continue;
    for (const item of category.items) {
      const origName = (item.originalName || '').toLowerCase().trim();
      const itemName = (item.name || '').toLowerCase().trim();

      if (origName === ingName || itemName === ingName) return item;
      if (origName && (origName.includes(ingName) || ingName.includes(origName))) return item;
      if (itemName && (itemName.includes(ingName) || ingName.includes(itemName))) return item;

      if (item.allOriginals?.some(orig => {
        const o = (orig || '').toLowerCase().trim();
        return o === ingName || o.includes(ingName) || ingName.includes(o);
      })) return item;
    }
  }
  return null;
};
